// @ts-nocheck
import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import {
  Paper,
  Table,
  TableBody,
  CircularProgress,
  Box,
  Skeleton,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';

import {
  useTableDispatch,
  useTableState,
} from '../../context/TableSchemaBuilderContext';
import Cell from '../Cell/Cell';
import ColumnHeader from '../Header/ColumnHeader';
import RowHeader from '../Header/RowHeader';
import CornerCell from '../Cell/CornerCell';
import { useTableModification } from '../../hooks/useTableModification';
import { useTableKeyboard } from '../../hooks/useTableKeyboard';
import { useCellRefs } from '../../hooks/useCellRefs';
import usePaginatedSchemaData from '../../../../hooks/Configuration/ConstantAttributes/usePaginatedSchemaData';
import { parseCellId, getColumnName } from '../../../../utils/Utils';

/**
 * Time-sliced virtualized table grid with infinite scrolling for paginated data
 */
const PaginatedTimeSlicedVirtualizedTableGrid = () => {
  const { schema, config, constantId } = useTableState();
  const dispatch = useTableDispatch();
  const { handleAddRowBefore, handleAddColumnBefore } = useTableModification();
  const { setCellRef } = useCellRefs();

  const containerRef = useRef();
  const [renderedCells, setRenderedCells] = useState([]);
  const [forceRefresh, setForceRefresh] = useState(0); // Add force refresh state
  const renderQueueRef = useRef([]);
  const renderingRef = useRef(false);
  const previousCellQueueKeyRef = useRef('');

  const readOnly = config?.readOnly || false;
  const DEFAULT_WIDTH = 100;
  const DEFAULT_HEIGHT = 40;

  // Initialize paginated data hook
  const { paginatedData, loadPage, loadInitialPage, getRowData, isRowLoaded } =
    usePaginatedSchemaData(constantId);

  useTableKeyboard();

  // Get current selection for scrolling to active cell
  const { ui } = useTableState();
  const currentCell = ui?.selection?.currentCell;

  // Track which pages have been applied to avoid re-applying
  const appliedPagesRef = useRef(new Set());

  // Load initial page when constantId is available
  useEffect(() => {
    if (constantId) {
      // Reset applied pages when constantId changes
      appliedPagesRef.current.clear();
      loadInitialPage();
    }
  }, [constantId, loadInitialPage]);

  // Update schema incrementally when new pages are loaded
  useEffect(() => {
    const currentLoadedPages = paginatedData.loadedPages;
    const newPages = new Set();

    // Find pages that haven't been applied yet
    for (const pageNum of currentLoadedPages) {
      if (!appliedPagesRef.current.has(pageNum)) {
        newPages.add(pageNum);
      }
    }

    // Apply only new pages to avoid refreshing existing cells
    if (newPages.size > 0) {
      for (const pageNum of newPages) {
        const pageSchema = paginatedData.pages.get(pageNum);
        if (pageSchema?.cells && Object.keys(pageSchema.cells).length > 0) {
          // Use the new action to incrementally add cells
          dispatch({
            type: 'ADD_PAGINATED_CELLS',
            payload: pageSchema,
          });

          // Mark this page as applied
          appliedPagesRef.current.add(pageNum);
        }
      }

      // After adding new pages, trigger a re-render of visible cells to replace loaders
      setForceRefresh((prev) => prev + 1);
    }
  }, [paginatedData.loadedPages, paginatedData.pages, dispatch]);

  // Set initial selection
  useEffect(() => {
    const firstCellId = 'A1';
    dispatch({
      type: 'UPDATE_SELECTION',
      payload: {
        currentCell: firstCellId,
        cells: [firstCellId],
        anchorCell: firstCellId,
      },
    });
  }, [dispatch]);

  // Gets column width from schema settings or uses default width
  const getColumnWidth = useCallback(
    (columnName) => {
      return schema?.columnSettings?.[columnName]?.width || DEFAULT_WIDTH;
    },
    [schema?.columnSettings]
  );

  // Calculate dimensions - use paginated data total rows when available
  const dataRows =
    paginatedData.totalRows > 0 ? paginatedData.totalRows : schema?.rows || 5;
  const dataColumns = schema?.columns || 5;
  const totalRows = readOnly ? dataRows : dataRows + 1;
  const totalColumns = readOnly ? dataColumns : dataColumns + 1;

  // Store latest pagination data in ref to avoid stale closures
  const paginatedDataRef = useRef(paginatedData);
  paginatedDataRef.current = paginatedData;

  // Handle scroll-based pagination
  const handleScroll = useCallback(
    (scrollOffset) => {
      if (!constantId) {
        return;
      }

      // Use ref to get fresh state and avoid stale closures
      const { totalPages: currentTotalPages, loadedPages: currentLoadedPages } =
        paginatedDataRef.current;

      // Don't return early if totalRows is 0 - we might still need to calculate based on totalPages
      // The API might return 0 items but still have pages to load
      // if (currentTotalRows === 0) return;

      // Calculate which row is at the scroll position (0-based index)
      const estimatedRowHeight = DEFAULT_HEIGHT;
      const currentRowIndex = Math.floor(scrollOffset / estimatedRowHeight);

      // Check if we need to load more data based on current scroll position
      const loadedPagesArray = Array.from(currentLoadedPages);

      if (loadedPagesArray.length === 0) {
        return;
      }

      // Calculate which page we need for the current row position
      const currentPageNeeded = Math.ceil((currentRowIndex + 1) / 100);

      // Always load the page we need if it's not already loaded
      if (
        currentPageNeeded <= currentTotalPages &&
        !currentLoadedPages.has(currentPageNeeded)
      ) {
        loadPage(currentPageNeeded);
      }

      // Preload next page if we're near the end of current page and not at the last page
      const currentPageProgress = ((currentRowIndex + 1) % 100) / 100;
      const nextPage = currentPageNeeded + 1;

      if (
        currentPageProgress > 0.8 && // Near end of current page (80%)
        nextPage <= currentTotalPages &&
        !currentLoadedPages.has(nextPage)
      ) {
        loadPage(nextPage);
      }
    },
    [constantId, loadPage]
  );

  // Virtualizers with proper resize support
  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => {
      if (index === 0 && !readOnly) return DEFAULT_HEIGHT;
      // Get custom row height from schema if available
      const rowHeight = schema?.rowSettings?.[index]?.height;
      return rowHeight || DEFAULT_HEIGHT;
    },
    overscan: 5, // Increased overscan for smoother scrolling
    onChange: (virtualizer) => {
      // Handle scroll for pagination
      const scrollElement = virtualizer.scrollElement;
      if (scrollElement) {
        handleScroll(scrollElement.scrollTop);
      }
    },
  });

  const columnVirtualizer = useVirtualizer({
    count: totalColumns,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => {
      if (index === 0 && !readOnly) return 50; // Row header width
      const dataColumnIndex = readOnly ? index + 1 : index;
      const columnName = getColumnName(dataColumnIndex);
      return getColumnWidth(columnName);
    },
    horizontal: true,
    overscan: 3,
  });

  /**
   * Time-sliced cell renderer for optimal performance in large virtualized tables
   *
   * This function implements a sophisticated time-slicing mechanism to prevent UI blocking
   * when rendering large numbers of table cells. Instead of rendering all cells at once
   * (which could freeze the browser), it processes cells in small batches over multiple
   * animation frames.
   *
   * Key Performance Optimizations:
   * - **Frame Budget Control**: Limits processing to 8ms per frame to maintain 60fps
   * - **Batch Processing**: Renders 15 cells at a time for optimal balance of speed/smoothness
   * - **Non-blocking**: Uses scheduler.postTask (when available) or setTimeout for async processing
   * - **Early Exit**: Prevents duplicate rendering attempts via renderingRef guard
   *
   * How it works:
   * 1. Checks if rendering is already in progress or queue is empty (early exit)
   * 2. Sets rendering flag to prevent concurrent execution
   * 3. Processes cells in batches within the frame budget (8ms)
   * 4. Updates rendered cells state with the current batch
   * 5. Schedules next batch processing if more cells remain
   * 6. Clears rendering flag when queue is empty
   *
   * Performance Benefits:
   * - Maintains responsive UI during large table renders
   * - Prevents main thread blocking that could cause janky scrolling
   * - Allows user interactions to remain smooth during cell rendering
   * - Optimizes for both speed (larger batches) and smoothness (time budgets)
   *
   * @dependencies
   * - renderingRef.current: Prevents concurrent rendering attempts
   * - renderQueueRef.current: Queue of cell data to be rendered
   * - setRenderedCells: State setter for accumulating rendered cell elements
   *
   * @see https://web.dev/optimize-long-tasks/ for time-slicing best practices
   */
  const renderCellsInBatches = useCallback(() => {
    // Guard clause: Exit if already rendering or no cells to process
    if (renderingRef.current || renderQueueRef.current.length === 0) {
      return;
    }

    // Set rendering flag to prevent concurrent execution
    renderingRef.current = true;

    // Performance constants for optimal frame utilization
    const BATCH_SIZE = 15; // Process 15 cells per batch for balanced performance
    const FRAME_BUDGET = 8; // 8ms budget per frame to maintain 60fps (16.67ms total)

    /**
     * Internal batch processor function
     * Processes cells within the allocated frame budget and schedules continuation
     */
    const processBatch = () => {
      const startTime = performance.now();
      const batch = [];
      let processedCount = 0;

      // Process cells while we have items, haven't hit batch limit, and are within time budget
      while (
        renderQueueRef.current.length > 0 &&
        processedCount < BATCH_SIZE &&
        performance.now() - startTime < FRAME_BUDGET
      ) {
        const cellData = renderQueueRef.current.shift();
        if (cellData) {
          batch.push(cellData);
          processedCount++;
        }
      }

      // Add the processed batch to rendered cells if we have any
      if (batch.length > 0) {
        setRenderedCells((prev) => [...prev, ...batch]);
      }

      // Schedule next batch if more cells remain in queue
      if (renderQueueRef.current.length > 0) {
        // Prefer modern scheduler API for better prioritization, fallback to setTimeout
        if (typeof scheduler !== 'undefined' && scheduler.postTask) {
          scheduler.postTask(processBatch, { priority: 'user-blocking' });
        } else {
          setTimeout(processBatch, 0);
        }
      } else {
        // All cells processed, clear rendering flag
        renderingRef.current = false;
      }
    };

    // Start the batch processing
    processBatch();
  }, []);

  // Helper function to create cell content based on position and data
  const createCellContent = useCallback(
    (i, j, actualRow, actualCol, columnName, cellId) => {
      // Check if we have data for this row
      const rowData = getRowData(actualRow - 1); // getRowData expects 0-based index
      const isLoaded = isRowLoaded(actualRow - 1);

      // Show loading state if data is not loaded
      if (!isLoaded && constantId) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: DEFAULT_HEIGHT,
              padding: 1,
            }}
          >
            <Skeleton
              variant="text"
              width="80%"
              height={20}
              animation="wave"
              sx={{
                fontSize: '0.875rem',
              }}
            />
          </Box>
        );
      }

      // Use paginated data if available, otherwise fall back to schema
      const cellValue =
        rowData?.[columnName] || schema?.cells?.[cellId]?.value || '';

      return (
        <Cell
          cellId={cellId}
          columnName={columnName}
          rowIndex={actualRow}
          setCellRef={setCellRef}
          heightFromVirtualized={DEFAULT_HEIGHT}
          value={cellValue}
          isFromPagination={!!rowData}
        />
      );
    },
    [getRowData, isRowLoaded, constantId, schema?.cells, setCellRef]
  );

  // Create cell data with pagination support
  const createCellData = useCallback(
    (virtualRow, virtualColumn) => {
      const i = virtualRow.index;
      const j = virtualColumn.index;
      let cellContent = null;
      let cellKey = '';

      if (!readOnly && i === 0 && j === 0) {
        cellKey = 'corner';
        cellContent = (
          <CornerCell
            onAddRowBefore={handleAddRowBefore}
            onAddColumnBefore={handleAddColumnBefore}
            heightFromVirtualized={DEFAULT_HEIGHT}
          />
        );
      } else if (!readOnly && i === 0) {
        cellKey = `col-${j}`;
        const columnName = getColumnName(j);
        cellContent = <ColumnHeader index={j} columnName={columnName} />;
      } else if (!readOnly && j === 0) {
        cellKey = `row-${i}`;
        cellContent = <RowHeader index={i} />;
      } else {
        const actualRow = readOnly ? i + 1 : i;
        const actualCol = readOnly ? j + 1 : j;
        const columnName = getColumnName(actualCol);
        const cellId = `${columnName}${actualRow}`;
        cellKey = cellId;

        cellContent = createCellContent(
          i,
          j,
          actualRow,
          actualCol,
          columnName,
          cellId
        );
      }

      return {
        key: cellKey,
        element: (
          <div
            key={cellKey}
            style={{
              position: 'absolute',
              top: virtualRow.start,
              left: virtualColumn.start,
              width: virtualColumn.size,
              height: virtualRow.size,
              boxSizing: 'border-box',
              overflow: 'visible',
              zIndex: !readOnly && (i === 0 || j === 0) ? 10 : 1,
            }}
          >
            {cellContent}
          </div>
        ),
      };
    },
    [readOnly, handleAddRowBefore, handleAddColumnBefore, createCellContent]
  );

  // Update cells when virtual items change
  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualColumns = columnVirtualizer.getVirtualItems();

  // Memoize cell queue creation with proper dependencies
  const cellQueue = useMemo(() => {
    const newQueue = [];
    for (const virtualRow of virtualRows) {
      for (const virtualColumn of virtualColumns) {
        newQueue.push(createCellData(virtualRow, virtualColumn));
      }
    }
    return newQueue;
  }, [virtualRows, virtualColumns, createCellData]);

  // Create a stable cell queue key to detect actual changes
  // Include forceRefresh to trigger re-render when new data loads
  const cellQueueKey = useMemo(() => {
    const rowsKey = virtualRows
      .map((r) => r.index + '-' + r.start + '-' + r.size)
      .join('|');
    const columnsKey = virtualColumns
      .map((c) => c.index + '-' + c.start + '-' + c.size)
      .join('|');
    return rowsKey + '_' + columnsKey + '_' + forceRefresh;
  }, [virtualRows, virtualColumns, forceRefresh]);

  useEffect(() => {
    // Only update cells when the virtual layout actually changes (scroll/resize)
    // Don't clear everything when pagination data changes
    const hasLayoutChanged = previousCellQueueKeyRef.current !== cellQueueKey;

    if (hasLayoutChanged) {
      previousCellQueueKeyRef.current = cellQueueKey;

      // Clear and update cells only when layout changes (not pagination)
      setRenderedCells([]);
      renderQueueRef.current = [];
      renderingRef.current = false;

      renderQueueRef.current = cellQueue;
      renderCellsInBatches();
    }
  }, [cellQueueKey, cellQueue, renderCellsInBatches]);

  // Scroll to selected cell
  useEffect(() => {
    if (currentCell && containerRef.current) {
      const [colIndex, rowIndex] = parseCellId(currentCell);
      const virtualRowIndex = readOnly ? rowIndex - 1 : rowIndex;
      const virtualColIndex = readOnly ? colIndex - 1 : colIndex;

      if (virtualRowIndex >= 0) {
        rowVirtualizer.scrollToIndex(virtualRowIndex, {
          align: 'auto',
          behavior: 'smooth',
        });
      }

      if (virtualColIndex >= 0) {
        columnVirtualizer.scrollToIndex(virtualColIndex, {
          align: 'auto',
          behavior: 'smooth',
        });
      }
    }
  }, [currentCell, rowVirtualizer, columnVirtualizer, readOnly, parseCellId]);

  // Measure virtualizers on mount and when schema changes
  useEffect(() => {
    if (containerRef.current) {
      rowVirtualizer.measure();
      columnVirtualizer.measure();
    }
  }, [rowVirtualizer, columnVirtualizer]);

  // Re-measure virtualizers when schema dimensions or settings change
  useEffect(() => {
    // Trigger re-measurement when column settings change (for resizing)
    if (rowVirtualizer && columnVirtualizer) {
      // Use a small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        rowVirtualizer.measure();
        columnVirtualizer.measure();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [
    schema?.columnSettings,
    schema?.rowSettings,
    rowVirtualizer,
    columnVirtualizer,
  ]);

  const totalHeight = rowVirtualizer.getTotalSize();
  const totalWidth = columnVirtualizer.getTotalSize();

  // Show loading indicator when loading data
  const showGlobalLoading =
    paginatedData.isLoading && paginatedData.loadedPages.size === 0;

  return (
    <Paper elevation={1} sx={{ height: '100%', width: '100%' }}>
      {showGlobalLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <div
        ref={containerRef}
        style={{
          height: 330,
          width: '100%',
          overflow: 'auto',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'auto',
          contain: 'layout style paint',
        }}
      >
        <Table
          sx={{
            tableLayout: 'fixed',
            width: totalWidth,
            '& .MuiFormControl-root.MuiTextField-root': {
              marginTop: 0,
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
          }}
        >
          <TableBody>
            <tr>
              <td
                colSpan={readOnly ? schema.columns : schema.columns + 1}
                style={{
                  padding: 0,
                  height: totalHeight,
                  verticalAlign: 'top',
                }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableBody>
                    {renderedCells.map((cell) => cell.element)}
                  </TableBody>
                </Table>
              </td>
            </tr>
          </TableBody>
        </Table>
      </div>

      {/* Loading indicator at the bottom when loading more pages */}
      {paginatedData.isLoading && paginatedData.loadedPages.size > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: 1,
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <CircularProgress size={20} />
          <span style={{ fontSize: '12px' }}>Loading more data...</span>
        </Box>
      )}
    </Paper>
  );
};

export default PaginatedTimeSlicedVirtualizedTableGrid;
